import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

// Connects to data-controller="filter-value"
// For datetime columns: initializes flatpickr on the value input.
// For dropdown columns: fetches unique values and shows a select instead of input.
export default class extends Controller {
  connect() {
    this.flatpickrInstance = null
    this.syncValueInput()
  }

  disconnect() {
    this.destroyFlatpickr()
  }

  columnChanged() {
    this.syncValueInput()
  }

  operatorChanged() {
    const columnSelect = this.element.querySelector("[data-filter-column]")
    const operatorSelect = this.element.querySelector("[data-filter-operator]")
    const input = this.element.querySelector("[data-filter-value]")
    if (!columnSelect || !input) return

    const column = columnSelect.value
    const columnTypes = this.getColumnTypes()
    const type = columnTypes[column]
    const typeStr = typeof type === "string" ? type : (type && String(type))
    if (typeStr === "datetime") {
      const isRange = operatorSelect?.value === "between"
      this.initFlatpickr(input, isRange)
    }
  }

  getDrawerData(key) {
    const drawer = this.element.closest("[data-controller*='filter-drawer']")
    if (!drawer) return null
    const attr = drawer.getAttribute(`data-filter-drawer-${key}-value`)
    if (!attr) return null
    try {
      return JSON.parse(attr)
    } catch {
      return attr
    }
  }

  getColumnTypes() {
    return this.getDrawerData("column-types") || {}
  }

  getDropdownColumns() {
    const cols = this.getDrawerData("dropdown-columns")
    return Array.isArray(cols) ? cols : []
  }

  getOptionsUrl() {
    const url = this.getDrawerData("options-url")
    return typeof url === "string" ? url : null
  }

  syncValueInput() {
    const columnSelect = this.element.querySelector("[data-filter-column]")
    const valueCell = this.element.querySelector("[data-filter-value-cell]")
    if (!columnSelect || !valueCell) return

    const column = columnSelect.value
    const dropdownColumns = this.getDropdownColumns()
    const optionsUrl = this.getOptionsUrl()
    const isDropdownColumn = column && dropdownColumns.includes(column) && optionsUrl

    this.destroyFlatpickr()

    if (isDropdownColumn) {
      this.showDropdown(valueCell, column, columnSelect)
    } else {
      this.showInput(valueCell, column, columnSelect)
    }
  }

  async showDropdown(valueCell, column, columnSelect) {
    const name = this.getValueInputName(valueCell)
    const currentValue = this.getCurrentValue(valueCell)

    valueCell.innerHTML = '<span class="loading loading-spinner loading-sm"></span>'

    try {
      const url = new URL(this.getOptionsUrl(), window.location.origin)
      url.searchParams.set("column", column)
      const res = await fetch(url.toString(), { headers: { "Accept": "application/json" } })
      const values = await res.json()

      const options = values.map((v) => {
        let val
        let text
        if (v != null && typeof v === "object" && !Array.isArray(v)) {
          val = String(v.value ?? v.label ?? "")
          text = String(v.label ?? v.value ?? "")
        } else {
          val = String(v ?? "")
          text = val
        }
        const sel = val === currentValue ? " selected" : ""
        return `<option value="${this.escapeHtml(val)}"${sel}>${this.escapeHtml(text)}</option>`
      }).join("")

      const selectHtml = `<select name="${this.escapeHtml(name)}" class="select select-bordered select-sm w-full" data-filter-value><option value="">Select...</option>${options}</select>`
      valueCell.innerHTML = selectHtml
    } catch {
      this.showInput(valueCell, column, columnSelect)
    }
  }

  showInput(valueCell, column, columnSelect) {
    const name = this.getValueInputName(valueCell)
    const currentValue = this.getCurrentValue(valueCell)

    valueCell.innerHTML = `<input type="text" name="${this.escapeHtml(name)}" value="${this.escapeHtml(currentValue)}" placeholder="Value..." class="input input-bordered input-sm w-full" data-filter-value />`

    const input = valueCell.querySelector("[data-filter-value]")
    const columnTypes = this.getColumnTypes()
    const type = columnTypes[column]
    const typeStr = typeof type === "string" ? type : (type && String(type))
    if (typeStr === "datetime" && input) {
      const operatorSelect = this.element.querySelector("[data-filter-operator]")
      const isRange = operatorSelect?.value === "between"
      this.initFlatpickr(input, isRange)
    }
  }

  getValueInputName(valueCell) {
    const input = valueCell.querySelector("[data-filter-value]")
    return input?.getAttribute("name") || "filter[0][value]"
  }

  getCurrentValue(valueCell) {
    const el = valueCell.querySelector("[data-filter-value]")
    return el?.value ?? ""
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str ?? ""
    return div.innerHTML
  }

  initFlatpickr(input, isRange = false) {
    this.destroyFlatpickr()
    this.flatpickrInstance = flatpickr(input, {
      mode: isRange ? "range" : "single",
      dateFormat: "Y-m-d",
      allowInput: true,
      disableMobile: false,
      static: true,
      position: "auto right"
    })
  }

  destroyFlatpickr() {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.destroy()
      this.flatpickrInstance = null
    }
  }
}
